import React, { useState, useEffect, useRef } from 'react'
import Thumbnail from "../../components/profile/recordVideointro/images/videoIntro.jpg";
import './videocontroller.css'
import Exitfullscreen from '../Icons/exitfullscreen';
import Fullscreenicon from '../Icons/fullscreenicon';
import Speakermuteicon from '../Icons/speakermuteicon';
import Speakerunmuteicon from '../Icons/speakerunmuteicon';
import Pauseicon from '../Icons/pauseicon';
import Playicon from '../Icons/playicon';
import { NotificationManager } from 'react-notifications';

const Videomanualcontroller = ({
    src,
    interviewDuration,
    violationAtSec
}) => {
    const videoRef = useRef(null);
    const playerRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);
    const [isSeeking, setIsSeeking] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isBuffering, setIsBuffering] = useState(false);
    const wasPlayingRef = useRef(false);
    const seekTimeRef = useRef(0);
    const prevViolationAtSecRef = useRef(violationAtSec);

    useEffect(() => {
        if (violationAtSec === undefined || violationAtSec === null) return;
        const timestampChanged = prevViolationAtSecRef.current !== violationAtSec;
        
        if (videoRef.current && timestampChanged) {
            const duration = videoRef.current.duration;
            const targetTime = Math.min(Math.max(0, violationAtSec), duration || interviewDuration || 0);
            
            if (videoRef.current.readyState >= 1) {
                
                videoRef.current.currentTime = targetTime;
                setCurrentTime(targetTime);
                
                const playPromise = videoRef.current.play();
                if (playPromise !== undefined) {
                    playPromise
                        .then(() => {
                            setIsPlaying(true);
                        })
                        .catch(err => {
                            console.error('Error playing video after seeking:', err);
                            NotificationManager.error('Unable to play video. Please try again later.', 'Playback Error', 3000);
                        });
                }
            } else {
                const onCanPlay = () => {
                    videoRef.current.currentTime = targetTime;
                    setCurrentTime(targetTime);
                    
                    const playPromise = videoRef.current.play();
                    if (playPromise !== undefined) {
                        playPromise
                            .then(() => {
                                setIsPlaying(true);
                            })
                            .catch(err => {
                                console.error('Error playing video after seeking:', err);
                                NotificationManager.error('Unable to play video. Please try again later.', 'Playback Error', 3000);
                            });
                    }
                    videoRef.current.removeEventListener('canplay', onCanPlay);
                };
                videoRef.current.addEventListener('canplay', onCanPlay);
            }
        }

        prevViolationAtSecRef.current = violationAtSec;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [violationAtSec]);

    // Video control handlers
    const togglePlayPause = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
                setIsPlaying(false);
            } else {
                videoRef.current.play().catch(err => {
                    console.error('Error playing video:', err);
                    NotificationManager.error('Unable to play video. Please try again later.', 'Playback Error', 3000);
                });
                setIsPlaying(true);
            }
        }
    };

    const handleVolumeChange = (newVolume) => {
        if (videoRef.current) {
            const vol = Math.max(0, Math.min(1, newVolume));
            videoRef.current.volume = vol;
            setVolume(vol);
            if (vol > 0) setIsMuted(false);
        }
    };

    const toggleMute = () => {
        if (videoRef.current) {
            videoRef.current.muted = !isMuted;
            setIsMuted(!isMuted);
        }
    };

    // Handle seek start - pause video to prevent buffering issues
    const handleSeekStart = () => {
        if (videoRef.current) {
            wasPlayingRef.current = isPlaying;
            if (isPlaying) {
                videoRef.current.pause();
            }
            setIsSeeking(true);
        }
    };

    // Handle seek change - update UI but don't seek video yet (for performance)
    const handleSeekChange = (time) => {
        seekTimeRef.current = time;
        setCurrentTime(time);
    };

    // Handle seek end - now actually seek the video
    const handleSeekEnd = () => {
        if (videoRef.current) {
            const seekTime = seekTimeRef.current;
            
            // Ensure video is ready to seek
            if (videoRef.current.readyState >= 1) {
                videoRef.current.currentTime = seekTime;
            }
            
            setIsSeeking(false);
            
            // Resume playing if it was playing before
            if (wasPlayingRef.current) {
                const playPromise = videoRef.current.play();
                if (playPromise !== undefined) {
                    playPromise.catch(err => {
                        console.error('Error resuming video after seek:', err);
                    });
                }
            }
        }
    };

    const handleTimeUpdate = () => {
        if (videoRef.current && !isSeeking) {
            setCurrentTime(videoRef.current.currentTime);
        }
    };

    const handleVideoEnded = () => {
        setIsPlaying(false);
    };

    // Handle video waiting/buffering state
    const handleWaiting = () => {
        setIsBuffering(true);
    };

    const handleCanPlay = () => {
        setIsBuffering(false);
    };

    const handleSeeked = () => {
        setIsBuffering(false);
    };

    const handlePlaying = () => {
        setIsPlaying(true);
        setIsBuffering(false);
    };

    const handlePause = () => {
        if (!isSeeking) {
            setIsPlaying(false);
        }
    };

    const toggleFullscreen = () => {
        if (!playerRef.current) return;

        if (!document.fullscreenElement) {
            playerRef.current.requestFullscreen().then(() => {
                setIsFullscreen(true);
            }).catch(err => {
                console.error('Error attempting to enable fullscreen:', err);
            });
        } else {
            document.exitFullscreen().then(() => {
                setIsFullscreen(false);
            });
        }
    };

    const formatDuration = (secs) => {
        if (secs == null || isNaN(secs)) return '00:00';
        const s = Math.floor(Number(secs)); 
        const hours = Math.floor(s / 3600);
        const mins = Math.floor((s % 3600) / 60);
        const seconds = s % 60;
        if (hours > 0) {
            return `${hours}:${mins.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        }
        return `${mins}:${seconds.toString().padStart(2, '0')}`;
    };

    // Listen for fullscreen changes
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, []);

    return (
        <div className="custom-video-player" ref={playerRef}>
            <video
                ref={videoRef}
                className="videoIntroThumbnail"
                src={src}
                poster={Thumbnail}
                preload="metadata"
                onTimeUpdate={handleTimeUpdate}
                onEnded={handleVideoEnded}
                onWaiting={handleWaiting}
                onCanPlay={handleCanPlay}
                onSeeked={handleSeeked}
                onPlaying={handlePlaying}
                onPause={handlePause}
                onClick={togglePlayPause}
            />
            {isBuffering && (
                <div className="video-buffering-indicator">
                    <div className="buffering-spinner"></div>
                </div>
            )}
            <div className="video-controls">
                <div className="progress-bar-container">
                    <input
                        type="range"
                        className="progress-bar"
                        min="0"
                        max={interviewDuration || 100}
                        step="0.1"
                        value={currentTime}
                        onChange={(e) => handleSeekChange(parseFloat(e.target.value))}
                        onMouseDown={handleSeekStart}
                        onMouseUp={handleSeekEnd}
                        onTouchStart={handleSeekStart}
                        onTouchEnd={handleSeekEnd}
                        style={{
                            '--progress-percent': `${(currentTime / (interviewDuration || 100)) * 100}%`
                        }}
                    />
                </div>
                <div className="controls-row">
                    <div className="left-controls">
                        <button className="control-btn" onClick={togglePlayPause}>
                            {isPlaying ? (
                                <Pauseicon />
                            ) : (
                                <Playicon />
                            )}
                        </button>
                        <span className="time-display">
                            {formatDuration(currentTime)} / {formatDuration(interviewDuration)}
                        </span>
                    </div>
                    <div className="right-controls">
                        <button className="control-btn" onClick={toggleMute}>
                            {isMuted || volume === 0 ? (
                                <Speakerunmuteicon />
                            ) : (
                                <Speakermuteicon />
                            )}
                        </button>
                        <input
                            type="range"
                            className="volume-slider"
                            min="0"
                            max="1"
                            step="0.1"
                            value={isMuted ? 0 : volume}
                            onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                            style={{
                                '--volume-percent': `${(isMuted ? 0 : volume) * 100}%`
                            }}
                        />
                        <button className="control-btn fullscreen-btn" onClick={toggleFullscreen}>
                            {isFullscreen ? (
                                <Exitfullscreen />
                            ) : (
                                <Fullscreenicon />
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Videomanualcontroller;